import { useSeoMeta } from '@unhead/react';

import { CardProvider, useCard } from '@/contexts/CardContext';
import { StepIndicator } from '@/components/StepIndicator';
import { Step1Token } from '@/components/steps/Step1Token';
import { Step2Design } from '@/components/steps/Step2Design';
import { Step3Print } from '@/components/steps/Step3Print';
import { PrintCard } from '@/components/PrintCard';

/** Screen-only wizard UI */
function ScreenContent() {
  const { step } = useCard();

  return (
    <div className="print-hide">
      <StepIndicator currentStep={step} />
      <div className="min-h-[60vh]">
        {step === 1 && <Step1Token />}
        {step === 2 && <Step2Design />}
        {step === 3 && <Step3Print />}
      </div>
    </div>
  );
}

/**
 * Print-only output, hidden on screen. Rendered at step 3.
 * Contains 2 landscape pages (10"x7" each):
 *   Page 1 = Outside (back cover + front cover)
 *   Page 2 = Inside  (QR redeem page + personalization)
 * Print double-sided, fold in half for a 5"x7" greeting card.
 */
function PrintOutput() {
  const { step, card } = useCard();
  if (step !== 3) return null;

  return (
    <div className="hidden print-card">
      <PrintCard card={card} />
    </div>
  );
}

export default function Index() {
  useSeoMeta({
    title: 'CashuCards - Bitcoin Greeting Cards',
    description: 'Create beautiful foldable greeting cards with Cashu eCash tokens. Print double-sided, fold in half for a 5x7 card with QR code inside!',
  });

  return (
    <CardProvider>
      {/* ===== Screen-only wrapper: hidden when printing ===== */}
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-amber-50/30 dark:to-amber-950/10 print-hide">
        {/* Header */}
        <header className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img
                src="/logo.png"
                alt="CashuCards"
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-lg font-bold tracking-tight">
                  CashuCards
                </h1>
                <p className="text-xs text-muted-foreground -mt-0.5">Bitcoin Greeting Cards</p>
              </div>
            </div>
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </div>
        </header>

        {/* Hero */}
        <section className="relative isolate overflow-hidden">
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute top-10 left-1/4 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl" />
            <div className="absolute top-20 right-1/4 w-56 h-56 bg-orange-400/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-rose-400/10 rounded-full blur-3xl" />
          </div>
          <div className="max-w-3xl mx-auto px-4 pt-10 pb-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium mb-4 border border-amber-200 dark:border-amber-800">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Bitcoin eCash Greeting Cards
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 bg-gradient-to-r from-amber-600 via-orange-500 to-rose-500 bg-clip-text text-transparent">
              Turn Bitcoin into Beautiful Cards
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
              Paste a Cashu token, choose your images, add a personal message, then print and fold into a beautiful greeting card with a scannable QR code inside.
            </p>
          </div>
        </section>

        {/* Wizard steps */}
        <div className="w-full max-w-5xl mx-auto px-4 pb-16">
          <ScreenContent />
        </div>

        {/* Footer */}
        <footer className="border-t border-border/50 py-6 text-center text-xs text-muted-foreground">
          <p>
            CashuCards &mdash; Create printable Bitcoin greeting cards.{' '}
            <a
              href="https://cashu.space"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors underline underline-offset-2"
            >
              Learn about Cashu
            </a>
          </p>
        </footer>
      </div>

      {/* ===== Print-only card: sibling of the screen wrapper ===== */}
      <PrintOutput />
    </CardProvider>
  );
}
